import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import repository from '@/data/repository.js';
import { isNonEmptyString } from '../helpers/utilsHelper.js';
import {
  ADMIN_TYPE_ID,
  WORKER_TYPE_ID,
  DEV_TYPE_ID,
  COORDINATOR_TYPE_ID,
  DRIVER_TYPE_ID,
  TRANSPORT_MANAGER_TYPE_ID,
  SUBCONTRACTOR_TYPE_ID,
  RADO_COORDINATOR_TYPE_ID
} from '../data/constants/authConstants.js';
import { LOGIN_ACCESS_USER_TYPES } from '../data/projectConfig.js';

export const useAuthStore = defineStore('auth', () => {
  // state
  const token = ref(localStorage.getItem('user-token') || '');
  const loggedInUserState = ref(localStorage.getItem('loggedInUserState'));
  const formInfo = ref('');

  // computed
  const isAuthenticated = computed(() => !!token.value);
  const loggedInUser = computed(() => getCurrentLoggedInUserState()?.loggedInUser ?? null);
  const loggedInUserTypeId = computed(() => loggedInUser.value?.type?.id ?? null);
  const isAdminLoggedIn = computed(() => loggedInUser.value?.type?.id === ADMIN_TYPE_ID);
  const isWorkerLoggedIn = computed(() => loggedInUser.value?.type?.id === WORKER_TYPE_ID);
  const isDeveloperLoggedIn = computed(() => loggedInUser.value?.type?.id === DEV_TYPE_ID);
  const isCoordinatorLoggedIn = computed(
    () => loggedInUser.value?.type?.id === COORDINATOR_TYPE_ID
  );
  const isDriverLoggedIn = computed(() => loggedInUser.value?.type?.id === DRIVER_TYPE_ID);
  const isTransportManagerLoggedIn = computed(
    () => loggedInUser.value?.type?.id === TRANSPORT_MANAGER_TYPE_ID
  );
  const isSubcontractorLoggedIn = computed(
    () => loggedInUser.value?.type?.id === SUBCONTRACTOR_TYPE_ID
  );
  const isRADOCCoordinatorLoggedIn = computed(
    () => loggedInUser.value?.type?.id === RADO_COORDINATOR_TYPE_ID
  );

  const loggedInUserSettings = computed(() =>
    isNonEmptyString(loggedInUser.value?.user_settings)
      ? JSON.parse(loggedInUser.value.user_settings)
      : {}
  );

  // actions
  function requestAuth(user) {
    return new Promise((resolve, reject) => {
      repository
        .get('/login', {
          auth: {
            username: user.username,
            password: user.password
          }
        })
        .then((response) => handleAuthResponse(response, resolve, reject))
        .catch((error) => handleAuthError(error, reject));
    });
  }

  function request2FAVerification(username, code) {
    return new Promise((resolve, reject) => {
      repository
        .post('/user/Confirm2FA', {
          email: username,
          code
        })
        .then((response) => handleAuthResponse(response, resolve, reject))
        .catch((error) => handleAuthError(error, reject));
    });
  }

  function handleAuthResponse(response, resolve, reject) {
    if (
      response?.data?.data?.type?.id != null &&
      !LOGIN_ACCESS_USER_TYPES.includes(response?.data?.data?.type?.id)
    ) {
      formInfo.value = 'Brak uprawnień.';
      reject('Brak uprawnień.');
      return;
    }

    const apiToken = response.data.data.api_token;
    localStorage.setItem('user-token', apiToken);
    token.value = apiToken;
    changeLoggedInUser(response.data.data);
    repository.defaults.headers.common['Authorization'] = 'Bearer ' + apiToken;
    resolve(response);
  }

  function handleAuthError(error, reject) {
    formInfo.value = error?.response?.data[0];
    localStorage.removeItem('user-token');
    reject(error);
  }

  function logOut() {
    return new Promise((resolve) => {
      localStorage.removeItem('user-token');
      changeLoggedInUser(null);
      delete repository.defaults.headers.common['Authorization'];
      token.value = null;
      resolve();
    });
  }

  function resetFormError() {
    formInfo.value = null;
  }

  function changeLoggedInUserFirstName(firstName) {
    let currentLoggedInUserState = getCurrentLoggedInUserState();
    currentLoggedInUserState.loggedInUser.firstName = firstName;
    loggedInUserState.value = JSON.stringify(currentLoggedInUserState);
    localStorage.setItem('loggedInUserState', loggedInUserState.value);
  }

  function changeLoggedInUserLastName(lastName) {
    let currentLoggedInUserState = getCurrentLoggedInUserState();
    currentLoggedInUserState.loggedInUser.lastName = lastName;
    loggedInUserState.value = JSON.stringify(currentLoggedInUserState);
    localStorage.setItem('loggedInUserState', loggedInUserState.value);
  }

  function changeLoggedInUser(user) {
    let currentLoggedInUserState = getCurrentLoggedInUserState();
    currentLoggedInUserState.loggedInUser = user;
    loggedInUserState.value = JSON.stringify(currentLoggedInUserState);
    localStorage.setItem('loggedInUserState', loggedInUserState.value);
  }

  async function updateUserSettings(settings) {
    return new Promise((resolve, reject) => {
      repository
        .post(`/user/${loggedInUser.value.id}/updateUserSettings`, {
          user_settings: settings
        })
        .then((response) => {
          console.log(response);
          if (response?.data?.data?.type?.id != null) {
            changeLoggedInUser(response.data.data);
          }
          resolve(response);
        })
        .catch((error) => {
          console.error(error);
          reject(error);
        });
    });
  }

  async function resetUserColumnsSettings() {
    try {
      const currentSettings = loggedInUserSettings.value || {};
      const newSettings = { ...currentSettings };

      if (newSettings.tables) {
        delete newSettings.tables;
      }

      await updateUserSettings(JSON.stringify(newSettings));
      return true;
    } catch (error) {
      console.error('resetUserColumnsSettings(): Failed to reset columns settings:', error);
      throw error;
    }
  }

  function getCurrentLoggedInUserState() {
    return loggedInUserState.value ? JSON.parse(loggedInUserState.value) : {};
  }

  function canAccess(access) {
    return access?.userTypesWithAccess?.includes(loggedInUserTypeId.value) ?? false;
  }

  return {
    formInfo,
    isAuthenticated,
    loggedInUser,
    isAdminLoggedIn,
    isWorkerLoggedIn,
    isDeveloperLoggedIn,
    isCoordinatorLoggedIn,
    isDriverLoggedIn,
    isTransportManagerLoggedIn,
    isSubcontractorLoggedIn,
    isRADOCCoordinatorLoggedIn,
    loggedInUserSettings,
    requestAuth,
    request2FAVerification,
    logOut,
    resetFormError,
    changeLoggedInUserFirstName,
    changeLoggedInUserLastName,
    canAccess,
    updateUserSettings,
    resetUserColumnsSettings
  };
});
